import { Pie } from "@ant-design/charts";
import { Skeleton } from "antd";
import { chunk } from "lodash";
import { Tooltip } from "common/components/tooltip/tooltip";
import { Flex } from "common/components/flex/flex";
import * as styles from "features/dashboard/sections/postOfferMetrics/pieChart/pieChart.module.scss";
import K from "utilities/constants";

const { FalloutReasonColors } = K;
const MAX_COLUMNS = 3;

const PieChartSkeleton = () => (
  <Flex align="center" justify="center" style={{ flex: 1 }}>
    <Skeleton.Avatar active shape="circle" size={240} />
  </Flex>
);

const CenterLabel = ({ total }) => (
  <Flex
    direction="column"
    align="center"
    gap={6}
    className={styles.pieChart__centerLabel}
  >
    <span className="text-secondary">Total</span>
    <span
      className={`font-weight-heavy text-primary ${styles.pieChart__centerValue}`}
    >
      {total}
    </span>
  </Flex>
);

const LegendItem = ({ type, color }) => (
  <div className={styles.pieChart__legendItem}>
    <span
      className={styles.pieChart__legendDot}
      style={{ backgroundColor: color }}
    />
    <Tooltip title={`${type}`}>
      <span className={styles.pieChart__legendLabel}>{type}</span>
    </Tooltip>
  </div>
);

const LegendColumn = ({ items }) => (
  <div className={styles.pieChart__legendColumn}>
    {items.map((item) => (
      <LegendItem
        key={item.type}
        type={item.type}
        value={item.value}
        color={item.color}
      />
    ))}
  </div>
);

const Legend = ({ chartData }) => {
  const chunkSize = Math.ceil(chartData.length / MAX_COLUMNS);
  const columns = chunk(chartData, chunkSize);

  return (
    <div className={styles.pieChart__legend}>
      {columns.map((column) => (
        <LegendColumn key={column[0].type} items={column} />
      ))}
    </div>
  );
};

export const PieChart = ({ falloutReasons = [], loading = false }) => {
  const chartData = falloutReasons
    .filter((r) => r.count > 0)
    .map((r) => ({
      type: r.name,
      value: r.count,
      color: FalloutReasonColors[r.name] ?? FalloutReasonColors.Empty,
    }))
    .sort((a, b) => b.value - a.value);

  const total = chartData.reduce((sum, d) => sum + d.value, 0);
  const isEmpty = chartData.length === 0 || total === 0;
  const dataCount = chartData.length;

  if (loading) {
    return <PieChartSkeleton />;
  }

  const chartHeightByDataCount = (count) => {
    if (count <= 3) return 240;
    if (count <= 12) return 220;
    return 200;
  };

  const chartSize = isEmpty ? 240 : chartHeightByDataCount(dataCount);

  const chartConfig = {
    width: chartSize,
    height: chartSize,
    data: isEmpty ? [{ type: "Empty", value: 1 }] : chartData,
    angleField: "value",
    colorField: "type",
    color: ({ type }) =>
      type === "Empty"
        ? FalloutReasonColors.Empty
        : FalloutReasonColors[type] ?? FalloutReasonColors.Empty,
    radius: 1,
    innerRadius: 0.6,
    legend: false,
    label: false,
    statistic: false,
    tooltip: isEmpty
      ? false
      : {
          fields: ["type", "value"],
          formatter: (datum) => {
            const percentage =
              total > 0 ? ((datum.value / total) * 100).toFixed(1) : "0";
            return {
              name: datum.type,
              value: `${datum.value} (${percentage}%)`,
            };
          },
          domStyles: {
            "g2-tooltip": {
              backgroundColor: "var(--surface-white)",
              color: "var(--text-base)",
              padding: "12px",
              boxShadow:
                "0 9px 28px 8px rgba(0, 0, 0, 0.05), 0 6px 16px 0 rgba(0, 0, 0, 0.08), 0 3px 6px -4px rgba(0, 0, 0, 0.12)",
              border: "none",
            },
          },
        },
    interactions: [],
  };

  return (
    <div
      className={`${styles.pieChart} ${isEmpty ? styles.pieChartEmpty : ""}`}
      style={{
        "--pie-chart-height": `${chartSize}px`,
      }}
    >
      <div
        className={`${styles.pieChart__container} ${
          isEmpty ? styles.pieChart__containerEmpty : ""
        }`}
      >
        <Pie {...chartConfig} />
        <CenterLabel total={isEmpty ? 0 : total} />
      </div>
      {!isEmpty && <Legend chartData={chartData} />}
    </div>
  );
};
